import {useEffect} from 'react';
import {useHistory, useLocation} from 'react-router-dom';
import Util from '../../../forecast-app/shared/util/util';
import {
	FEATURE_FLAG_UI_STORAGE_KEY,
	featureFlagUrl,
	pathForFeatureFlagUi,
	resolveStoredFeatureFlagUi,
} from './featureFlagUiPreference';

/**
 * The feature flags entry point. Renders nothing: it reads which of the two screens the operator last
 * chose and sends them there.
 *
 * This sits on the path the Mission Control header tab and every existing bookmark already point at,
 * which is why the switch lives here rather than on either screen. Moving the newer screens onto that
 * path instead would have meant moving three routes, the paths their Cypress selectors name, and the
 * spec — where the original page is one flat route and was cheap to move aside.
 *
 * Only this ambiguous entry point consults the preference. Both screens keep a URL of their own, and
 * asking for one of those directly always renders it, whatever is stored — otherwise an operator whose
 * preference disagreed with the link they just followed could not get where they were going.
 *
 * `replace`, not push, so the entry point leaves no history entry of its own: Back from a feature flag
 * screen should leave the feature altogether rather than land here and be bounced forward again.
 *
 * Navigating from an effect rather than by rendering react-router's `Redirect`. That component only
 * navigates from its mount lifecycle, and this route is reached by transitions that re-render it
 * instead of remounting it — arriving from the screen it is about to send the operator back to, which
 * is exactly what the header tab does from either screen. A `Redirect` renders, does nothing, and
 * leaves a blank page on the entry path. The effect re-runs whenever the resolved destination changes,
 * so it is right on a first mount and on every later render alike.
 */
export function FeatureFlagsUiRedirect(): null {
	const {pathname} = useLocation();
	const history = useHistory();
	const stored = Util.localStorageGetItemWithDefault(FEATURE_FLAG_UI_STORAGE_KEY, '');
	const to = featureFlagUrl(pathname, pathForFeatureFlagUi(resolveStoredFeatureFlagUi(stored)));

	useEffect(() => {
		history.replace(to);
	}, [history, to]);

	return null;
}
